import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "./firebase";
import { collection, getDocs, getDoc, doc, updateDoc, addDoc, deleteDoc, serverTimestamp } from "firebase/firestore";

function MenuEditorOverlay({ ordine, localeTag, onClose, isNew = false, tavoloNumero = null }) {
  const [user] = useAuthState(auth);
  const [nomeOperatore, setNomeOperatore] = useState("");

  const [menu, setMenu] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [categoriaSelezionata, setCategoriaSelezionata] = useState("");
  const [carrello, setCarrello] = useState([]);
  const [messaggio, setMessaggio] = useState("");
  const [noteTemporanee, setNoteTemporanee] = useState({});

  // ── Carica nome operatore ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchNome = async () => {
      if (!user) return;
      const snap = await getDocs(collection(db, "utenti"));
      const trovato = snap.docs.find(d => d.id === user.uid);
      if (trovato) {
        setNomeOperatore(trovato.data().nome || trovato.data().name || "");
      }
    };
    fetchNome();
  }, [user]);

  // ── Carica menu ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchMenu = async () => {
      const snapshot = await getDocs(collection(db, "menu"));
      const lista = snapshot.docs
        .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
        .filter(item =>
          item.localeTag?.trim().toLowerCase() === localeTag.trim().toLowerCase()
        );

      setMenu(lista);

      const cats = [...new Set(lista.map(p => p.categoria).filter(Boolean))];
      setCategorie(cats.map((c, i) => ({ id: i, nome: c })));
      if (cats.length > 0) setCategoriaSelezionata(cats[0]);
    };

    fetchMenu();
  }, [localeTag]);

  // ── Inizializza carrello con ordine esistente ─────────────────────────────
  useEffect(() => {
    if (!ordine || menu.length === 0) return;

    // itemStati è la fonte di verità per-piatto scritta dalla cucina
    // Nuovo approccio: aggrega tutti gli items da tutti gli ordini originali (versione dettagliata)
    const tuttiItems = ordine.ordiniOriginali?.flatMap(o =>
      (o.items || []).map((item, idx) => ({
        id: item.dishId || item.id || item.name,
        uid: item.uid || `${o.id}_${idx}`,
        stato:
          item.stato ||
          o.itemStati?.[String(idx)] ||
          o.stato ||
          "inviato",
        nome: item.name || item.nome || "",
        prezzo: item.price ?? item.prezzo ?? 0,
        quantita: item.quantity ?? item.quantita ?? 1,
        categoria: item.categoria || "",
        custom: item.custom || "",
        origineOrdineId: o.id
      }))
    ) || [];

    setCarrello(tuttiItems);
  }, [ordine, menu]);

  // ── Helper quantità piatto nel carrello ───────────────────────────────────
  const getQuantitaPiatto = (piattoId) => {
    return carrello
      .filter(item => item.id === piattoId)
      .reduce((sum, item) => sum + item.quantita, 0);
  };

  // ── Aggiungi al carrello ──────────────────────────────────────────────────
  const aggiungiAlCarrello = (piatto) => {
    const nota = noteTemporanee[piatto.id] || "";

    setCarrello(prev => {
      const index = prev.findIndex(
        item =>
          item.id === piatto.id &&
          (item.custom || "") === (nota || "") &&
          item.stato === "inviato"
      );

      if (index !== -1) {
        // 🔥 aumenta quantità invece di duplicare
        const updated = [...prev];
        updated[index].quantita += 1;
        return updated;
      }

      // nuovo item
      return [
        ...prev,
        {
          id: piatto.id,
          uid: Date.now().toString() + "_" + Math.random().toString(36).slice(2),
          stato: "inviato",
          nome: piatto.nome,
          prezzo: piatto.prezzo,
          quantita: 1,
          categoria: piatto.categoria,
          custom: nota
        }
      ];
    });

    setNoteTemporanee(prev => ({ ...prev, [piatto.id]: "" }));
  };

  // ── Modifica quantità ─────────────────────────────────────────────────────
  const modificaQuantita = (uid, nuova) => {
    if (nuova <= 0) {
      setCarrello(prev => prev.filter(p => p.uid !== uid));
      return;
    }

    setCarrello(prev =>
      prev.map(p =>
        p.uid === uid
          ? {
              ...p,
              quantita: nuova,
              stato: p.stato || "inviato"
            }
          : p
      )
    );
  };

  // ── Salva modifiche ───────────────────────────────────────────────────────
const salvaModifiche = async () => {
  // 🔥 BLOCCO LOCALE
  const localeRef = doc(db, "locali", localeTag);
  const localeSnap = await getDoc(localeRef);

  if (localeSnap.exists() && localeSnap.data().stato === "bloccato") {
    setMessaggio("🚫 Locale disattivato");
    return;
  }
  try {
    const itemsFormattati = carrello.map(item => ({
      dishId: item.id || null,
      uid: item.uid || (Date.now().toString() + "_" + Math.random().toString(36).slice(2)),
      nome: item.nome || "",
      name: item.nome || "",
      prezzo: item.prezzo ?? 0,
      price: item.prezzo ?? 0,
      quantita: item.quantita ?? 1,
      quantity: item.quantita ?? 1,
      categoria: item.categoria || "",
      custom: item.custom || "",
      stato: item.stato || "inviato"
    }));

    if (itemsFormattati.length === 0) {
      setMessaggio("❌ Ordine vuoto");
      return;
    }

    const totale = itemsFormattati.reduce(
      (sum, item) => sum + parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10),
      0
    );

    const STATI_PRIORITA = ["inviato", "in attesa", "in preparazione", "pronto", "servito"];

    const statoGlobale =
      STATI_PRIORITA.find((s) =>
        itemsFormattati.some((item) => String(item.stato).toLowerCase() === s)
      ) || "inviato";

    const itemStati = {};
    itemsFormattati.forEach((item, idx) => {
      itemStati[String(idx)] = item.stato || "inviato";
    });

    const tableFinale = ordine?.table || tavoloNumero;
    const chiaveTavolo = `${localeTag.toLowerCase().replace(/[^a-z0-9]/gi, "")}_${tableFinale}`;

    if (isNew) {
      await addDoc(collection(db, "ordini"), {
        locale: localeTag,
        table: tableFinale,
        chiaveTavolo,
        items: itemsFormattati,
        piatti: itemsFormattati,
        totale,
        stato: statoGlobale,
        itemStati,
        timestamp: serverTimestamp(),
        cameriere_id: nomeOperatore || "GESTORE",

        // 🔥 AGGIUNGI QUESTO
        vistoInCucina: false
      });
    } else {
      const ordiniOriginali = ordine?.ordiniOriginali || [];
      const ordinePrincipaleId =
        ordine?.id || 
        [...ordiniOriginali]
          .sort((a, b) => {
            const ta = a.timestamp?.seconds || 0;
            const tb = b.timestamp?.seconds || 0;
            return ta - tb;
          })[0]?.id;

      if (!ordinePrincipaleId) {
        throw new Error("ordinePrincipaleId mancante");
      }

      await updateDoc(doc(db, "ordini", ordinePrincipaleId), {
          items: itemsFormattati,
          piatti: itemsFormattati,
          totale,
          stato: statoGlobale,
          itemStati,

          // 🔥 FONDAMENTALE
          vistoInCucina: false
        });

      const altriOrdini = ordiniOriginali.filter(o => o.id !== ordinePrincipaleId);

      if (altriOrdini.length > 0) {
        await Promise.all(
          altriOrdini.map(o => deleteDoc(doc(db, "ordini", o.id)))
        );
      }
    }

    setMessaggio("✅ Ordine salvato!");
    setTimeout(() => {
      setMessaggio("");
      onClose();
    }, 1200);
  } catch (err) {
    console.error(err);
    setMessaggio("❌ Errore salvataggio");
  }
};

  // ── Totale (usato nel footer) ─────────────────────────────────────────────
  const totale = carrello.reduce(
    (sum, item) => sum + parseFloat(item.prezzo) * item.quantita, 0
  );

  // ── Render ────────────────────────────────────────────────────────────────
  /*
   * Layout a 3 zone con flex-column e altezza fissa.
   *   ① Header  — flexShrink:0, non scrolla mai
   *   ② Body    — flex:1, overflowY:auto — unica zona scrollabile
   *   ③ Footer  — flexShrink:0, sempre visibile — qui sta il bottone Salva
   *
   * height: "calc(100vh - 90px - 2rem)" corrisponde allo spazio disponibile
   * nel wrapper di DashboardGestore (top:90px, padding:1rem sopra e sotto).
   */
  return (
    <div
      style={{
        background: "linear-gradient(180deg, #121212 0%, #0f0f0f 100%)",
        color: "#fff",
        borderRadius: "26px",
        boxShadow: "0 40px 120px rgba(0,0,0,0.75)",
        border: "1px solid rgba(255,255,255,0.06)",
        width: "100%",
        maxWidth: "600px",
        height: "calc(100dvh - 140px)",
        maxHeight: "calc(100dvh - 140px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >

      {/* ① HEADER — fisso */}
      <div
        style={{
          flexShrink: 0,
          padding: "1.2rem 1.4rem 0.8rem",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {/* Titolo + pulsante chiudi */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
            {isNew
              ? `➕ Nuovo Ordine – Tavolo ${tavoloNumero}`
              : `✏️ Modifica – Tavolo ${ordine?.table}`}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#1f1f1f",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#aaa",
              fontSize: "1.1rem",
              borderRadius: "50%",
              width: "34px",
              height: "34px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Tab categorie — scroll orizzontale */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            overflowY: "hidden",
            paddingBottom: "4px",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          {categorie.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoriaSelezionata(cat.nome)}
              style={{
                flexShrink: 0,
                background: categoriaSelezionata === cat.nome ? "#2ecc71" : "#1e1e1e",
                color: categoriaSelezionata === cat.nome ? "#000" : "#ccc",
                border: "1px solid rgba(255,255,255,0.05)",
                padding: "7px 15px",
                borderRadius: "999px",
                fontSize: "0.84rem",
                fontWeight: categoriaSelezionata === cat.nome ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {cat.nome}
            </button>
          ))}
        </div>
      </div>

      {/* ② BODY — unica zona scrollabile */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem 1.4rem 120px",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Lista piatti */}
        {menu
          .filter(p => p.categoria === categoriaSelezionata)
          .map(piatto => (
            <div
              key={piatto.id}
              style={{
                background: "#171717",
                padding: "1rem",
                borderRadius: "16px",
                marginBottom: "0.75rem",
                border: getQuantitaPiatto(piatto.id) > 0
                  ? "1px solid rgba(46,204,113,0.35)"
                  : "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <strong style={{ fontSize: "0.95rem" }}>{piatto.nome}</strong>
                  {getQuantitaPiatto(piatto.id) > 0 && (
                    <span style={{
                      background: "#2ecc71",
                      color: "#000",
                      fontSize: "0.68rem",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      fontWeight: 700,
                    }}>
                      {getQuantitaPiatto(piatto.id)}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ opacity: 0.6, fontSize: "0.9rem" }}>{piatto.prezzo} €</span>
                  <button
                    onClick={() => aggiungiAlCarrello(piatto)}
                    style={{
                      background: "#0a84ff",
                      color: "#fff",
                      border: "none",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
              <textarea
                placeholder="Modifica / Nota..."
                value={noteTemporanee[piatto.id] || ""}
                onChange={e => setNoteTemporanee(prev => ({ ...prev, [piatto.id]: e.target.value }))}
                rows={1}
                style={{
                  width: "100%",
                  marginTop: "8px",
                  padding: "7px 10px",
                  borderRadius: "8px",
                  border: "1px solid #2c2c2e",
                  background: "#1a1a1a",
                  color: "#fff",
                  fontSize: "0.84rem",
                  resize: "vertical",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
              />
            </div>
          ))}

        {/* Carrello */}
        {carrello.length > 0 && (
          <div style={{
            marginTop: "1.25rem",
            background: "#151515",
            padding: "1.1rem",
            borderRadius: "18px",
            border: "1px solid rgba(255,255,255,0.05)",
          }}>
            <div style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#aaa",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              marginBottom: "0.75rem",
            }}>
              Carrello
            </div>

            {carrello.map(item => (
              <div
                key={item.uid}
                style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem", gap: "8px" }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.88rem", fontWeight: 600 }}>
                    {item.nome} × {item.quantita}
                  </div>

                  <div
                    style={{
                      fontSize: "0.72rem",
                      marginTop: "2px",
                      fontWeight: 600,
                      color:
                        item.stato === "pronto"
                          ? "#4CAF50"
                          : item.stato === "in preparazione"
                          ? "#FF9800"
                          : item.stato === "servito"
                          ? "#9E9E9E"
                          : "#888"
                    }}
                  >
                    ● {item.stato || "inviato"}
                  </div>
                  {item.custom && (
                    <div style={{ fontSize: "0.75rem", color: "#FF9800", marginTop: "2px" }}>
                      {item.custom}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => modificaQuantita(item.uid, item.quantita - 1)}
                  style={{ background: "#444", color: "#fff", border: "none", padding: "4px 8px", borderRadius: "8px", cursor: "pointer" }}
                >
                  −
                </button>
                <button
                  onClick={() => modificaQuantita(item.uid, item.quantita + 1)}
                  style={{ background: "#444", color: "#fff", border: "none", padding: "4px 8px", borderRadius: "8px", cursor: "pointer" }}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ③ FOOTER — sempre visibile in fondo */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "0.6rem 1rem calc(1.2rem + env(safe-area-inset-bottom, 20px))",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          background: "#121212",
          zIndex: 20,
        }}
      >
        {messaggio && (
          <p style={{
            margin: "0 0 0.6rem",
            textAlign: "center",
            fontSize: "0.88rem",
            color: messaggio.startsWith("✅") ? "#2ecc71" : "#e74c3c",
            fontWeight: 600,
          }}>
            {messaggio}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <span style={{ fontSize: "0.82rem", color: "#aaa" }}>
            {carrello.reduce((s, i) => s + i.quantita, 0)} piatti
          </span>
          <span style={{ fontSize: "1.1rem", fontWeight: 700, color: "#2ecc71" }}>
            {totale.toFixed(2)} €
          </span>
        </div>

        <button
          onClick={salvaModifiche}
          disabled={carrello.length === 0}
          style={{
            width: "100%",
            padding: "14px",
            background: carrello.length === 0
              ? "#2c2c2e"
              : "linear-gradient(90deg, #2ecc71, #27ae60)",
            color: carrello.length === 0 ? "#555" : "#000",
            border: "none",
            borderRadius: "14px",
            fontWeight: 700,
            fontSize: "0.95rem",
            cursor: carrello.length === 0 ? "not-allowed" : "pointer",
            transition: "all 0.15s ease",
          }}
        >
          {isNew ? "Invia ordine" : "💾 Salva modifiche"}
        </button>
      </div>

    </div>
  );
}

export default MenuEditorOverlay;
